import './App.css';
import { Suspense, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Profile from "./pages/Profile";
import WorkTrialRoute from "./pages/WorkTrialRoute";
import Interview from "./pages/Interview";
import PrescreeningInterview from "./pages/PrescreeningInterview";
import ProfileInterview from "./pages/ProfileInterview";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TestCatalog from "./pages/TestCatalog";
import TestApplication from "./pages/TestApplication";
import TestCycle from "./pages/TestCycle";
import TestEntries from "./pages/TestEntries";
import TestEnrolments from "./pages/TestEnrolments";
import TestCertificates from "./pages/TestCertificates";
import TestCheckoutSuccess from "./pages/TestCheckoutSuccess";
import TestCheckoutCancelled from "./pages/TestCheckoutCancelled";
import {Navigate, Route, BrowserRouter as Router, Routes} from "react-router-dom";
import AuthProvider from "./components/AuthProvider";
import PopupManager from "./components/PopupManager";
import ChunkErrorBoundary from "./components/workspace/ChunkErrorBoundary";
import { lazyWithReload } from "./utils/lazyLoad";
import { HandbookPageRoute, HandbookRoute, WalkthroughRoute } from "./walkthroughs/loader";
import { initGA, trackPageView } from "./utils/analytics";
import { adminViewTrialIdFromPath } from "./config/adminViewSession";

/* The workspace is the app's only split route tree: it carries a second design
   system and its own component set, and no Appli-level page has any reason to
   download either. Everything above it stays in the main chunk exactly as it is. */
const WorkspaceLayout = lazyWithReload(() => import('./pages/workspace/WorkspaceLayout'));
const WorkspaceHome = lazyWithReload(() => import('./pages/workspace/screens/Home'));
const WorkspaceChat = lazyWithReload(() => import('./pages/workspace/screens/Chat'));
const WorkspaceTasks = lazyWithReload(() => import('./pages/workspace/screens/Tasks'));
const WorkspaceDocs = lazyWithReload(() => import('./pages/workspace/screens/Docs'));
const WorkspaceWorkLog = lazyWithReload(() => import('./pages/workspace/screens/WorkLog'));
const WorkspaceVideo = lazyWithReload(() => import('./pages/workspace/screens/VideoSubmissions'));
const WorkspaceTeam = lazyWithReload(() => import('./pages/workspace/screens/Team'));
const WorkspaceAnnouncements = lazyWithReload(() => import('./pages/workspace/screens/Announcements'));
const AdminViewWorkspace = lazyWithReload(() => import('./pages/workspace/AdminViewWorkspace'));
/* The Handbook and the walkthrough page are the two screens that live in the
   walkthroughs chunk rather than the workspace's: the shell, the blocks and the six
   scenes' registry ship with them, and nothing else in the workspace needs them
   until a walkthrough is opened. */
const WorkspaceHandbook = HandbookRoute;
const WorkspaceWalkthrough = WalkthroughRoute;
const WorkspaceHandbookPage = HandbookPageRoute;

// Initialize GA4 once when the module loads
initGA();

// Component to track page views on route changes
function PageViewTracker() {
    const location = useLocation();

    useEffect(() => {
        /* An admin looking at a candidate's workspace is not a candidate page view. Left in,
           every screen an admin opened would land in the candidate portal's own GA property,
           carrying the work trial id in the path — admin traffic counted as product usage, and
           a trial id sent to a third party that has no reason to hold one.

           Read off the path, not off `isAdminView()`: this component sits above the routes and
           runs before AdminViewWorkspace has entered the mode, so the module flag is still
           false for the very first page view — the one that would carry the trial id. */
        if (adminViewTrialIdFromPath(location.pathname)) return;
        trackPageView(location.pathname + location.search);
    }, [location]);

    return null;
}

/* A workspace URL that names no screen — the bare route, or anything unknown —
   is not a screen: it lands on Home. Built absolutely from the id so a splat
   match cannot change what "home" resolves against, and from the root the URL is
   already under so an admin view redirects within itself rather than bouncing the
   frame to the candidate route. */
function WorkspaceHomeRedirect({ root = '/workspace' }) {
    const { workTrialId } = useParams();
    return <Navigate to={`${root}/${workTrialId}/home`} replace />;
}

/* The workspace's screens, defined once and mounted under both roots.

   `/workspace/:id` is the candidate's own. `/admin-view/:id` is the same tree
   rendered for an admin who is not the candidate — same layout, same screens, same
   styling, because the requirement is that an admin sees what the candidate sees and
   a second copy of this list is a second thing to keep in step. A screen added here
   reaches both, which is the point: the drift this avoids is the whole reason the
   admin portal frames this app instead of porting it. */
function workspaceScreens(root) {
    return (
        <>
            <Route index element={<WorkspaceHomeRedirect root={root}/>}/>
            <Route path="home" element={<WorkspaceHome/>}/>
            <Route path="chat" element={<WorkspaceChat/>}/>
            <Route path="chat/:conversationId" element={<WorkspaceChat/>}/>
            <Route path="tasks" element={<WorkspaceTasks/>}/>
            <Route path="tasks/:cardId" element={<WorkspaceTasks/>}/>
            <Route path="docs" element={<WorkspaceDocs/>}/>
            <Route path="handbook" element={<WorkspaceHandbook/>}/>
            {/* A "How we work here" page (plan §15.4): read like any other screen,
                under the Handbook's row. Before the walkthrough route, because a
                two-segment static path is not a walkthrough id. */}
            <Route path="handbook/pages/:pageId" element={<WorkspaceHandbookPage/>}/>
            {/* One walkthrough, as a page of the workspace. It belongs to the
                Handbook: the rail keeps that row lit and the shell reads its page
                key off the first segment, as it does for a conversation or a card. */}
            <Route path="handbook/:walkthroughId" element={<WorkspaceWalkthrough/>}/>
            <Route path="work-log" element={<WorkspaceWorkLog/>}/>
            <Route path="video" element={<WorkspaceVideo/>}/>
            <Route path="team" element={<WorkspaceTeam/>}/>
            <Route path="announcements" element={<WorkspaceAnnouncements/>}/>
            <Route path="*" element={<WorkspaceHomeRedirect root={root}/>}/>
        </>
    );
}

/* The workspace chunk's own guard: a stale tab that outlived a deploy reloads
   once rather than showing a blank stage. */
function WorkspaceBoundary({ children }) {
    const location = useLocation();
    return (
        <ChunkErrorBoundary pathname={location.pathname}>
            <Suspense fallback={null}>{children}</Suspense>
        </ChunkErrorBoundary>
    );
}

function App() {
    return (
        <Router>
            <PageViewTracker />
            <AuthProvider>
                <PopupManager />
                <Routes>
                    <Route path="/" element={<Home/>}/>
                    <Route path="/login" element={<Login/>}/>
                    <Route path="/signup" element={<Signup/>}/>
                    <Route path="/profile" element={<Profile/>}/>
                    <Route path="/work-trial/:workTrialId" element={<WorkTrialRoute/>}/>
                    {/* The eval4 renderer, beside the legacy one rather than over it. */}
                    <Route
                        path="/workspace/:workTrialId"
                        element={<WorkspaceBoundary><WorkspaceLayout/></WorkspaceBoundary>}
                    >
                        {workspaceScreens('/workspace')}
                    </Route>
                    {/* The same workspace, opened by an admin for a trial that is not theirs.
                        Read-only, and enforced as such by the candidate API rather than by
                        anything on this route: see src/config/adminViewSession.js. */}
                    <Route
                        path="/admin-view/:workTrialId"
                        element={<WorkspaceBoundary><AdminViewWorkspace/></WorkspaceBoundary>}
                    >
                        {workspaceScreens('/admin-view')}
                    </Route>
                    <Route path="/interview/:workTrialId/:interviewId" element={<Interview/>}/>
                    <Route path="/pre-screening-interview/:interviewId" element={<PrescreeningInterview/>}/>
                    <Route path="/profile-interview" element={<ProfileInterview/>}/>
                    <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                    <Route path="/tests" element={<TestCatalog/>}/>
                    <Route path="/tests/application" element={<TestApplication/>}/>
                    <Route path="/tests/cycles/:cycleId" element={<TestCycle/>}/>
                    <Route path="/tests/entries" element={<TestEntries/>}/>
                    <Route path="/tests/enrolments" element={<TestEnrolments/>}/>
                    <Route path="/tests/certificates" element={<TestCertificates/>}/>
                    {/* Stripe's return urls — the paths are set by the backend's
                        checkout session (SUCCESS_PATH / CANCEL_PATH). */}
                    <Route path="/tests/checkout/success" element={<TestCheckoutSuccess/>}/>
                    <Route path="/tests/checkout/cancelled" element={<TestCheckoutCancelled/>}/>
                </Routes>
            </AuthProvider>
        </Router>
    );
}

export default App;
